<!-- Page Header -->
<div class="page-header">
  <div class="header-content">
    <div class="header-main">
      <button class="btn btn-outline-secondary btn-sm"
              (click)="goBack()"
              [disabled]="isLoading"
              style="margin-right: 10px;">
        <i class="fa-solid fa-arrow-left"></i>
      </button>
      <div class="header-icon">
        @if (inventory_id) {
          <i class="fa-solid fa-pen-to-square"></i>
        } @else {
          <i class="fa-solid fa-circle-plus"></i>
        }
      </div>
      <div class="header-text">
        <h1 class="page-title">
          @if (inventory_id) {
            Edit Inventory Item
          } @else {
            Create New Inventory
          }
        </h1>
        <p class="page-description">
          @if (inventory_id) {
            Update inventory item details and manage stock information
          } @else {
            Add new items to your inventory with complete details and pricing
          }
        </p>
      </div>
    </div>
    <div class="header-actions">
      <button class="btn btn-outline-secondary"
              (click)="goBack()"
              [disabled]="isLoading">
        <i class="fa-solid fa-xmark"></i>
        Cancel
      </button>
    </div>
  </div>
</div>

<!-- Alert Message -->
@if (successMessage) {
  <div style="margin-bottom: 20px;">
    <ngb-alert #selfClosingAlert [type]="type" (closed)="successMessage = ''">
      <div class="d-flex align-items-center">
        <i class="fas fa-check-circle me-2" *ngIf="type === 'success'"></i>
        <i class="fas fa-exclamation-triangle me-2" *ngIf="type === 'danger'"></i>
        {{ message }}
      </div>
    </ngb-alert>
  </div>
}

<!-- Form Content -->
<form [formGroup]="inventoryForm" (ngSubmit)="onSubmit()">
  <div class="row g-4">
    <!-- Left Column - Form Fields -->
    <div class="col-lg-8">

      <!-- Basic Information Card -->
      <div class="card" style="margin-bottom: 20px;">
        <div style="padding: 16px 20px; border-bottom: 1px solid var(--border); background: var(--bg-muted); border-radius: var(--radius-lg) var(--radius-lg) 0 0;">
          <div style="display: flex; align-items: center; gap: 10px;">
            <div style="width: 30px; height: 30px; border-radius: var(--radius-sm); background: var(--accent-subtle); color: var(--accent); display: flex; align-items: center; justify-content: center; font-size: 13px;">
              <i class="fa-solid fa-circle-info"></i>
            </div>
            <div>
              <div style="font-size: 14px; font-weight: 600; color: var(--text-primary);">Basic Information</div>
              <div style="font-size: 12px; color: var(--text-tertiary);">Enter the basic details of the inventory item</div>
            </div>
          </div>
        </div>
        <div style="padding: 20px;">
          <div class="row g-3">
            <div class="col-md-6">
              <div class="form-group">
                <label for="name" class="form-label">
                  <i class="fa-solid fa-tag me-1"></i>
                  Item Name *
                </label>
                <input type="text"
                       class="form-control"
                       formControlName="name"
                       placeholder="Enter item name"
                       [disabled]="isLoading">
                @if (inventoryForm.get('name')?.invalid && inventoryForm.get('name')?.touched) {
                  <div class="invalid-feedback d-block">
                    <i class="fa-solid fa-circle-exclamation me-1"></i>
                    Item name is required
                  </div>
                }
              </div>
            </div>
            <div class="col-md-6">
              <div class="form-group">
                <label for="category_id" class="form-label">
                  <i class="fa-solid fa-folder me-1"></i>
                  Category *
                </label>
                <select class="form-select"
                        formControlName="category_id"
                        [disabled]="isLoading || !categories?.length">
                  <option value="">Select category</option>
                  @for (item of categories; track item.id) {
                    <option [value]="item.id">{{item.name}}</option>
                  }
                </select>
                @if (inventoryForm.get('category_id')?.invalid && inventoryForm.get('category_id')?.touched) {
                  <div class="invalid-feedback d-block">
                    <i class="fa-solid fa-circle-exclamation me-1"></i>
                    Please select a category
                  </div>
                }
              </div>
            </div>
          </div>

          @if (!inventory_id) {
            <div class="row g-3" style="margin-top: 4px;">
              <div class="col-md-4">
                <div class="form-group">
                  <label for="quantity" class="form-label">
                    <i class="fa-solid fa-boxes-stacked me-1"></i>
                    Initial Quantity *
                  </label>
                  <input type="number"
                         class="form-control"
                         formControlName="quantity"
                         min="0"
                         placeholder="0"
                         [disabled]="isLoading">
                </div>
              </div>
              <div class="col-md-4">
                <div class="form-group">
                  <label for="receipt_number" class="form-label">
                    <i class="fa-solid fa-receipt me-1"></i>
                    Receipt Number *
                  </label>
                  <input type="text"
                         class="form-control"
                         formControlName="receipt_number"
                         placeholder="Enter receipt number"
                         [disabled]="isLoading">
                </div>
              </div>
              <div class="col-md-4">
                <div class="form-group">
                  <label for="expiry_date" class="form-label">
                    <i class="fa-solid fa-calendar-days me-1"></i>
                    Expiry Date *
                  </label>
                  <input type="date"
                         class="form-control"
                         formControlName="expiry_date"
                         [min]="today"
                         [disabled]="isLoading">
                </div>
              </div>
            </div>
          }
        </div>
      </div>

      <!-- Pricing Information Card -->
      <div class="card" style="margin-bottom: 20px;">
        <div style="padding: 16px 20px; border-bottom: 1px solid var(--border); background: var(--bg-muted); border-radius: var(--radius-lg) var(--radius-lg) 0 0;">
          <div style="display: flex; align-items: center; gap: 10px;">
            <div style="width: 30px; height: 30px; border-radius: var(--radius-sm); background: var(--success-bg); color: var(--success-text); display: flex; align-items: center; justify-content: center; font-size: 13px;">
              <i class="fa-solid fa-naira-sign"></i>
            </div>
            <div>
              <div style="font-size: 14px; font-weight: 600; color: var(--text-primary);">Pricing Information</div>
              <div style="font-size: 12px; color: var(--text-tertiary);">Set cost price, selling price, and price tags</div>
            </div>
          </div>
        </div>
        <div style="padding: 20px;">
          <div class="row g-3">
            <div class="col-md-6">
              <div class="form-group">
                <label for="cost_price" class="form-label">
                  <i class="fa-solid fa-money-bill-wave me-1"></i>
                  Cost Price *
                </label>
                <div class="input-group">
                  <span class="input-group-text" style="background: var(--bg-muted); border-color: var(--input-border); color: var(--text-secondary); font-weight: 600;">&#8358;</span>
                  <input type="number"
                         class="form-control"
                         formControlName="cost_price"
                         min="1"
                         placeholder="0.00"
                         [disabled]="isLoading">
                </div>
              </div>
            </div>
            <div class="col-md-6">
              <div class="form-group">
                <label for="selling_price" class="form-label">
                  <i class="fa-solid fa-tags me-1"></i>
                  Selling Price *
                </label>
                <div class="input-group">
                  <span class="input-group-text" style="background: var(--bg-muted); border-color: var(--input-border); color: var(--text-secondary); font-weight: 600;">&#8358;</span>
                  <input type="number"
                         class="form-control"
                         formControlName="selling_price"
                         min="1"
                         placeholder="0.00"
                         [disabled]="isLoading">
                </div>
              </div>
            </div>
          </div>

          <!-- Price Tags Section -->
          <div style="margin-top: 20px; padding-top: 20px; border-top: 1px solid var(--border);">
            <div style="margin-bottom: 12px;">
              <div style="font-size: 13px; font-weight: 600; color: var(--text-primary);">
                <i class="fa-solid fa-tags me-2" style="color: var(--accent);"></i>
                Price Tag Selling Prices
              </div>
              <div style="font-size: 12px; color: var(--text-tertiary); margin-top: 2px;">Set different selling prices for various price tags</div>
            </div>
            @if (priceTags && priceTags.length) {
              <div class="table-responsive">
                <table class="table" style="margin-bottom: 0;">
                  <thead>
                    <tr>
                      <th>Tag Name</th>
                      <th style="width: 200px;">Selling Price (&#8358;)</th>
                    </tr>
                  </thead>
                  <tbody>
                    @for (tag of priceTags; track tag.id; let i = $index) {
                      <tr>
                        <td>
                          <div style="display: flex; align-items: center; gap: 8px;">
                            <i class="fa-solid fa-tag" style="color: var(--accent); font-size: 12px;"></i>
                            <span style="font-weight: 500;">{{ tag.name }}</span>
                          </div>
                        </td>
                        <td>
                          <div class="input-group input-group-sm">
                            <span class="input-group-text" style="background: var(--bg-muted); border-color: var(--input-border); color: var(--text-secondary);">&#8358;</span>
                            <input type="number"
                                   class="form-control"
                                   [value]="priceTagPrices[i] ? priceTagPrices[i].selling_price : 0"
                                   (input)="updatePriceTagPrice(i, $any($event.target).value)"
                                   min="0"
                                   [placeholder]="'0.00'"
                                   [disabled]="isLoading">
                          </div>
                        </td>
                      </tr>
                    }
                  </tbody>
                </table>
              </div>
            } @else {
              <div style="text-align: center; padding: 24px; color: var(--text-tertiary);">
                <i class="fa-solid fa-tags" style="font-size: 24px; margin-bottom: 8px; opacity: 0.4;"></i>
                <p style="margin: 0; font-size: 13px;">No price tags available</p>
              </div>
            }
          </div>
        </div>
      </div>

      <!-- Stock Management Card -->
      <div class="card" style="margin-bottom: 20px;">
        <div style="padding: 16px 20px; border-bottom: 1px solid var(--border); background: var(--bg-muted); border-radius: var(--radius-lg) var(--radius-lg) 0 0;">
          <div style="display: flex; align-items: center; gap: 10px;">
            <div style="width: 30px; height: 30px; border-radius: var(--radius-sm); background: var(--warning-bg); color: var(--warning-text); display: flex; align-items: center; justify-content: center; font-size: 13px;">
              <i class="fa-solid fa-warehouse"></i>
            </div>
            <div>
              <div style="font-size: 14px; font-weight: 600; color: var(--text-primary);">Stock Management</div>
              <div style="font-size: 12px; color: var(--text-tertiary);">Configure stock limits and alerts</div>
            </div>
          </div>
        </div>
        <div style="padding: 20px;">
          <div class="row g-3">
            <div class="col-md-6">
              <div class="form-group">
                <label for="low_stock" class="form-label">
                  <i class="fa-solid fa-triangle-exclamation me-1"></i>
                  Low Stock Alert Limit *
                </label>
                <input type="number"
                       class="form-control"
                       formControlName="low_stock"
                       min="1"
                       placeholder="10"
                       [disabled]="isLoading">
                <div style="font-size: 12px; color: var(--text-tertiary); margin-top: 6px;">
                  <i class="fa-solid fa-circle-info me-1"></i>
                  Get notified when stock falls below this level
                </div>
              </div>
            </div>
            <div class="col-md-6">
              <div class="form-group">
                <label for="expiry_date_limit" class="form-label">
                  <i class="fa-solid fa-clock me-1"></i>
                  Expiry Alert (Days) *
                </label>
                <input type="number"
                       class="form-control"
                       formControlName="expiry_date_limit"
                       min="1"
                       placeholder="30"
                       [disabled]="isLoading">
                <div style="font-size: 12px; color: var(--text-tertiary); margin-top: 6px;">
                  <i class="fa-solid fa-circle-info me-1"></i>
                  Get notified this many days before expiry
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Right Column - File Uploads -->
    <div class="col-lg-4">

      <!-- Product Image Upload Card -->
      <div class="card" style="margin-bottom: 20px;">
        <div style="padding: 16px 20px; border-bottom: 1px solid var(--border); background: var(--bg-muted); border-radius: var(--radius-lg) var(--radius-lg) 0 0;">
          <div style="display: flex; align-items: center; gap: 10px;">
            <div style="width: 30px; height: 30px; border-radius: var(--radius-sm); background: var(--info-bg); color: var(--info-text); display: flex; align-items: center; justify-content: center; font-size: 13px;">
              <i class="fa-solid fa-image"></i>
            </div>
            <div>
              <div style="font-size: 14px; font-weight: 600; color: var(--text-primary);">Product Image</div>
              <div style="font-size: 12px; color: var(--text-tertiary);">Upload item photo</div>
            </div>
          </div>
        </div>
        <div style="padding: 20px;">
          @if (uploadProgress()) {
            <div style="margin-bottom: 16px;">
              <ngb-progressbar [value]="uploadProgress()" type="primary" />
              <div style="text-align: center; font-size: 12px; color: var(--text-secondary); margin-top: 6px;">Uploading image... {{ uploadProgress() }}%</div>
            </div>
          }

          <div style="border: 2px dashed var(--border); border-radius: var(--radius-lg); padding: 24px; text-align: center; cursor: pointer; transition: all 0.2s ease; background: var(--bg-muted);"
               [class.success]="uploadSuccess"
               [class.error]="uploadError"
               (drop)="onFileDrop($event)"
               (dragover)="onDragOver($event)"
               (click)="fileInput.click()">
            @if (imagePreview() || inventory_details?.main_image) {
              <div style="position: relative;">
                <img [src]="imagePreview() || inventory_details?.main_image"
                     alt="Product Preview"
                     style="max-width: 100%; max-height: 180px; border-radius: var(--radius-md); object-fit: cover;">
                <div style="position: absolute; inset: 0; background: rgba(0,0,0,0.4); border-radius: var(--radius-md); display: flex; flex-direction: column; align-items: center; justify-content: center; opacity: 0; transition: opacity 0.2s;"
                     onmouseenter="this.style.opacity='1'" onmouseleave="this.style.opacity='0'">
                  <i class="fa-solid fa-camera" style="color: white; font-size: 20px;"></i>
                  <span style="color: white; font-size: 12px; margin-top: 4px;">Click to change</span>
                </div>
              </div>
            } @else {
              <div>
                <i class="fa-solid fa-cloud-arrow-up" style="font-size: 32px; color: var(--text-tertiary); margin-bottom: 10px;"></i>
                <div style="font-size: 14px; font-weight: 600; color: var(--text-primary); margin-bottom: 4px;">Upload Product Image</div>
                <div style="font-size: 12px; color: var(--text-tertiary); margin-bottom: 4px;">Drag & drop or click to select</div>
                <div style="font-size: 11px; color: var(--text-tertiary);">JPG, PNG up to 5MB</div>
              </div>
            }
            <input type="file"
                   accept=".jpg,.jpeg,.png"
                   (change)="onFileChange($event)"
                   hidden
                   #fileInput
                   [disabled]="isLoading">
          </div>

          @if (imageName()) {
            <div style="display: flex; align-items: center; justify-content: space-between; padding: 10px 12px; background: var(--bg-muted); border-radius: var(--radius-md); margin-top: 12px; border: 1px solid var(--border);">
              <div style="display: flex; align-items: center; gap: 8px; font-size: 12px; overflow: hidden;">
                <i class="fa-solid fa-file-image" style="color: var(--info-text);"></i>
                <span style="font-weight: 500; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">{{ imageName() }}</span>
                <span style="color: var(--text-tertiary);">({{ fileSize() }} KB)</span>
              </div>
              <button type="button"
                      class="btn btn-sm"
                      style="color: var(--danger-text); padding: 2px 6px;"
                      (click)="removeImage()"
                      [disabled]="isLoading">
                <i class="fa-solid fa-trash-can" style="font-size: 12px;"></i>
              </button>
            </div>
          }
        </div>
      </div>

      <!-- Receipt Upload Card (Only for new items) -->
      @if (!inventory_id) {
        <div class="card" style="margin-bottom: 20px;">
          <div style="padding: 16px 20px; border-bottom: 1px solid var(--border); background: var(--bg-muted); border-radius: var(--radius-lg) var(--radius-lg) 0 0;">
            <div style="display: flex; align-items: center; gap: 10px;">
              <div style="width: 30px; height: 30px; border-radius: var(--radius-sm); background: var(--warning-bg); color: var(--warning-text); display: flex; align-items: center; justify-content: center; font-size: 13px;">
                <i class="fa-solid fa-file-lines"></i>
              </div>
              <div>
                <div style="font-size: 14px; font-weight: 600; color: var(--text-primary);">Purchase Receipt</div>
                <div style="font-size: 12px; color: var(--text-tertiary);">Upload receipt document</div>
              </div>
            </div>
          </div>
          <div style="padding: 20px;">
            @if (uploadProgress2()) {
              <div style="margin-bottom: 16px;">
                <ngb-progressbar [value]="uploadProgress2()" type="primary" />
                <div style="text-align: center; font-size: 12px; color: var(--text-secondary); margin-top: 6px;">Uploading receipt... {{ uploadProgress2() }}%</div>
              </div>
            }

            <div style="border: 2px dashed var(--border); border-radius: var(--radius-lg); padding: 24px; text-align: center; cursor: pointer; transition: all 0.2s ease; background: var(--bg-muted);"
                 [class.success]="uploadSuccess"
                 [class.error]="uploadError"
                 (drop)="onFileDrop2($event)"
                 (dragover)="onDragOver2($event)"
                 (click)="fileInput2.click()">
              @if (showFile()) {
                <div style="position: relative;">
                  <i class="fa-solid fa-file-circle-check" style="font-size: 48px; color: var(--success-text);"></i>
                  <div style="position: absolute; inset: 0; display: flex; flex-direction: column; align-items: center; justify-content: center; opacity: 0; transition: opacity 0.2s;"
                       onmouseenter="this.style.opacity='1'" onmouseleave="this.style.opacity='0'">
                    <i class="fa-solid fa-arrow-up-from-bracket" style="color: var(--text-primary); font-size: 20px;"></i>
                    <span style="color: var(--text-primary); font-size: 12px; margin-top: 4px;">Click to change</span>
                  </div>
                </div>
              } @else {
                <div>
                  <i class="fa-solid fa-file-arrow-up" style="font-size: 32px; color: var(--text-tertiary); margin-bottom: 10px;"></i>
                  <div style="font-size: 14px; font-weight: 600; color: var(--text-primary); margin-bottom: 4px;">Upload Receipt</div>
                  <div style="font-size: 12px; color: var(--text-tertiary); margin-bottom: 4px;">Drag & drop or click to select</div>
                  <div style="font-size: 11px; color: var(--text-tertiary);">PDF, DOC, TXT, Images</div>
                </div>
              }
              <input type="file"
                     accept=".doc,.docx,.pdf,.txt,.png,.jpg,.jpeg"
                     (change)="onFileChange2($event)"
                     hidden
                     #fileInput2
                     [disabled]="isLoading">
            </div>

            @if (fileName()) {
              <div style="display: flex; align-items: center; justify-content: space-between; padding: 10px 12px; background: var(--bg-muted); border-radius: var(--radius-md); margin-top: 12px; border: 1px solid var(--border);">
                <div style="display: flex; align-items: center; gap: 8px; font-size: 12px; overflow: hidden;">
                  <i class="fa-solid fa-file" style="color: var(--warning-text);"></i>
                  <span style="font-weight: 500; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">{{ fileName() }}</span>
                  <span style="color: var(--text-tertiary);">({{ fileSize2() }} KB)</span>
                </div>
                <button type="button"
                        class="btn btn-sm"
                        style="color: var(--danger-text); padding: 2px 6px;"
                        (click)="removeFile()"
                        [disabled]="isLoading">
                  <i class="fa-solid fa-trash-can" style="font-size: 12px;"></i>
                </button>
              </div>
            }
          </div>
        </div>
      }
    </div>
  </div>

  <!-- Submit Actions -->
  <div class="card" style="margin-top: 4px;">
    <div style="padding: 20px; display: flex; align-items: center; justify-content: flex-end; gap: 12px;">
      <button type="button"
              class="btn btn-outline-secondary"
              (click)="goBack()"
              [disabled]="isLoading">
        <i class="fa-solid fa-xmark"></i>
        Cancel
      </button>
      <button type="submit"
              class="btn btn-primary"
              [disabled]="inventoryForm.invalid || isLoading"
              style="min-width: 180px;">
        @if (isLoading) {
          <div class="spinner-border spinner-border-sm" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
          @if (inventory_id) {
            Updating Inventory...
          } @else {
            Creating Inventory...
          }
        } @else {
          <i class="fa-solid fa-floppy-disk"></i>
          @if (inventory_id) {
            Update Inventory Item
          } @else {
            Create Inventory Item
          }
        }
      </button>
    </div>
  </div>
</form>
